import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Trophy, Medal, Users, Filter, Calendar } from 'lucide-react';
// FASE 8 — grade da lista (POS · ATLETA · EQUIPE · PART · PESO · PERFOR · medalhas · PTS)
import { buildRankingGrid, componentLegend } from '../lib/rankingColumns';
import { publicRankingApi, federationApi } from '../lib/api';
import { Button } from '../components/ui/button';
import SearchableCombobox from '../components/SearchableCombobox';

/* ───────── helpers ───────── */

// Busca do combobox ignorando acentos/caixa ("pre" acha "PRÉ-MIRIM").
const normalizeForSearch = (text) =>
  (text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-5 py-2 rounded-full text-sm font-medium transition-all border ${
      active
        ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
        : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#D4AF37]/40 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const placeBadge = (place) => {
  const map = {
    1: { bg: 'bg-[#D4AF37]', txt: 'text-black', label: '1º' },
    2: { bg: 'bg-[#C0C0C0]', txt: 'text-black', label: '2º' },
    3: { bg: 'bg-[#CD7F32]', txt: 'text-black', label: '3º' },
  };
  const s = map[place] || { bg: 'bg-[#27272A]', txt: 'text-[#A1A1AA]', label: `${place}º` };
  return (
    <span className={`inline-flex items-center justify-center min-w-[24px] sm:min-w-[40px] px-1 sm:px-2 py-0.5 rounded-full text-micro sm:text-xs font-bold ${s.bg} ${s.txt}`}>
      {s.label}
    </span>
  );
};

/* ───────── component ───────── */

export default function PublicRankingConsolidatedPage() {
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('athletes');

  // Filters — nenhuma seleção no estado inicial (Ano + Federação são o gatilho mínimo)
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(''); // '' | number
  const [champId, setChampId] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [federationId, setFederationId] = useState('');
  const [federations, setFederations] = useState([]);
  const [modality, setModality] = useState(''); // '' | 'gi' | 'nogi'
  // FASE 8 — composição do total (PART/PESO/PERFOR) e 4º ícone (2º Bronze):
  // vêm da RESPOSTA da API; sem eles, as colunas não aparecem.
  const [bonusComponents, setBonusComponents] = useState(false);
  const [secondBronze, setSecondBronze] = useState(false);

  // Ano + Federação definem o universo inicial do ranking
  const ready = Boolean(year && federationId);

  // ── Load championships for the selected federation + year ────────────────
  const loadChampionships = useCallback(async () => {
    if (!year || !federationId) {
      setChampionships([]);
      return;
    }
    try {
      const { data } = await publicRankingApi.getChampionshipsByYear(year, {
        federation_id: federationId,
      });
      setChampionships(data || []);
    } catch {
      setChampionships([]);
    }
  }, [year, federationId]);

  useEffect(() => { loadChampionships(); }, [loadChampionships]);

  // ── Load federations (filtro Federação — Etapa 12) ────────────────────────
  useEffect(() => {
    federationApi.list()
      .then(({ data }) => setFederations(Array.isArray(data) ? data : []))
      .catch(() => setFederations([]));
  }, []);

  // ── Load ranking — somente com Ano + Federação selecionados ───────────────
  const load = useCallback(async () => {
    if (!year || !federationId) {
      setAthletes([]);
      setTeams([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { year, federation_id: federationId };
      if (champId) params.championship_id = champId;
      if (ageGroup) params.age_group = ageGroup;
      if (modality) params.modality = modality;
      const { data } = await publicRankingApi.getConsolidated(params);
      setAthletes(data?.athletes || []);
      setTeams(data?.teams || []);
      // FASE 8 — as colunas de composição (PART/PESO/PERFOR) só existem no
      // ranking federativo (livro de pontos) e o 4º ícone (2º Bronze) só
      // aparece quando o campeonato/ano tem o 2º Bronze ligado. Quem decide é a
      // RESPOSTA — a tela nunca inventa coluna vazia.
      setBonusComponents(Boolean(data?.bonus_components_enabled));
      setSecondBronze(Boolean(data?.second_bronze_enabled));
    } catch {
      setAthletes([]);
      setTeams([]);
      setBonusComponents(false);
      setSecondBronze(false);
    } finally {
      setLoading(false);
    }
  }, [year, champId, ageGroup, federationId, modality]);

  useEffect(() => { load(); }, [load]);

  // ── Sorted teams ─────────────────────────────────────────────────────────
  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) =>
      b.gold - a.gold || b.silver - a.silver || b.bronze - a.bronze || a.team_name.localeCompare(b.team_name)
    ),
    [teams],
  );

  // ── FASE 8 — grade da lista de atletas (ordem e larguras) ────────────────
  // POS · ATLETA · EQUIPE · PART · PESO · PERFOR · 🥇🥈🥉(2ºB) · PTS
  // No celular, Equipe e composição ficam ocultas (mesmo tratamento que a
  // coluna Equipe já tinha); as medalhas e o PTS permanecem.
  const grid = useMemo(
    () => buildRankingGrid({ showComponents: bonusComponents, showSecondBronze: secondBronze }),
    [bonusComponents, secondBronze],
  );
  const legend = useMemo(() => componentLegend(bonusComponents), [bonusComponents]);

  // ── Year options ─────────────────────────────────────────────────────────
  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= currentYear - 5; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  // Opções dos comboboxes pesquisáveis (Ano / Federação)
  const yearOptions = useMemo(
    () => years.map((y) => ({ value: String(y), label: String(y) })),
    [years],
  );
  const federationOptions = useMemo(
    () => federations.map((f) => ({ value: f._id, label: f.name })),
    [federations],
  );

  /* ─────── RENDER ─────── */

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="public-ranking-consolidated">

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 py-2 sm:py-4 flex items-center gap-2 sm:gap-3">
          <Link
            to="/public"
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#D4AF37] transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
          </Link>
          <div className="min-w-0">
            <p className="text-micro sm:text-xs text-[#71717A] uppercase tracking-wider">Ranking Geral</p>
            <h1 className="text-sm sm:text-lg font-bold text-white font-['Chivo'] truncate">
              Ranking de Campeonatos
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-8">

        {/* Title */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Trophy className="w-5 h-5 sm:w-7 sm:h-7 text-[#D4AF37]" />
          <h2 className="text-lg sm:text-3xl font-bold text-white font-['Chivo']">
            Ranking Geral
          </h2>
          {ready && (
            <span className="text-[#A1A1AA] text-xs sm:text-sm">
              · {athletes.length} atletas · {teams.length} equipes
            </span>
          )}
        </div>

        {/* Tabs — primeira escolha do usuário */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-6" data-testid="ranking-tabs">
          <TabButton active={mode === 'athletes'} onClick={() => setMode('athletes')}>
            <Users size={16} className="inline mr-1.5" /> Atletas
          </TabButton>
          <TabButton active={mode === 'teams'} onClick={() => setMode('teams')}>
            <Trophy size={16} className="inline mr-1.5" /> Equipes
          </TabButton>
        </div>

        {/* Filters — Ano | Federação | Campeonato | Agrupamento | Modalidade */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 items-end" data-testid="ranking-filters">

          {/* Year */}
          <div className="flex flex-col gap-1">
            <label className="text-micro sm:text-xs text-[#71717A] uppercase tracking-wider font-semibold flex items-center gap-1">
              <Calendar size={12} /> Ano
            </label>
            <SearchableCombobox
              value={year === '' ? '' : String(year)}
              onChange={(v) => { setYear(v ? Number(v) : ''); setChampId(''); }}
              options={yearOptions}
              placeholder="Selecione o ano"
              searchPlaceholder="Digitar ano..."
              emptyText="Nenhum ano encontrado"
              testId="filter-year"
              allowClear
              className="h-auto w-[120px] sm:w-[140px] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-[#141414] border-[#27272A] text-xs sm:text-sm"
            />
          </div>

          {/* Federação (Etapa 12) */}
          <div className="flex flex-col gap-1">
            <label className="text-micro sm:text-xs text-[#71717A] uppercase tracking-wider font-semibold flex items-center gap-1">
              <Filter size={12} /> Federação
            </label>
            <SearchableCombobox
              value={federationId}
              onChange={(v) => { setFederationId(v); setChampId(''); }}
              options={federationOptions}
              placeholder="Selecione a federação"
              searchPlaceholder="Digitar federação..."
              emptyText="Nenhuma federação encontrada"
              testId="filter-federation"
              allowClear
              keywords={(opt) => normalizeForSearch(opt.label)}
              className="h-auto w-[160px] sm:w-[200px] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-[#141414] border-[#27272A] text-xs sm:text-sm"
            />
          </div>

          {/* Championship — depende de Ano + Federação */}
          <div className="flex flex-col gap-1">
            <label className="text-micro sm:text-xs text-[#71717A] uppercase tracking-wider font-semibold flex items-center gap-1">
              <Trophy size={12} /> Campeonato
            </label>
            <select
              value={champId}
              onChange={(e) => setChampId(e.target.value)}
              disabled={!ready}
              className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-[#141414] border border-[#27272A] text-white text-xs sm:text-sm focus:border-[#D4AF37] focus:outline-none min-w-[140px] sm:min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="filter-championship"
            >
              <option value="">Todos os campeonatos</option>
              {championships.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Age group — comportamento atual preservado */}
          <div className="flex flex-col gap-1">
            <label className="text-micro sm:text-xs text-[#71717A] uppercase tracking-wider font-semibold flex items-center gap-1">
              <Users size={12} /> Agrupamento
            </label>
            <select
              value={ageGroup}
              onChange={(e) => setAgeGroup(e.target.value)}
              className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-[#141414] border border-[#27272A] text-white text-xs sm:text-sm focus:border-[#D4AF37] focus:outline-none"
              data-testid="filter-age-group"
            >
              <option value="">Todos</option>
              <option value="kids">🧒 Kids</option>
              <option value="adults">🧑 Adultos</option>
            </select>
          </div>

          {/* Modalidade (Etapa 12) */}
          <div className="flex flex-col gap-1">
            <label className="text-micro sm:text-xs text-[#71717A] uppercase tracking-wider font-semibold flex items-center gap-1">
              <Medal size={12} /> Modalidade
            </label>
            <select
              value={modality}
              onChange={(e) => setModality(e.target.value)}
              className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-[#141414] border border-[#27272A] text-white text-xs sm:text-sm focus:border-[#D4AF37] focus:outline-none"
              data-testid="filter-modality"
            >
              <option value="">Todas</option>
              <option value="gi">🥋 Kimono (Gi)</option>
              <option value="nogi">👕 No-Gi</option>
            </select>
          </div>

          {/* Active filter chips */}
          {(year || federationId || champId || ageGroup || modality) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setYear(''); setFederationId(''); setChampId(''); setAgeGroup(''); setModality(''); }}
              className="text-[#A1A1AA] hover:text-white border border-[#27272A] ml-1"
              data-testid="clear-filters"
            >
              ✕ Limpar filtros
            </Button>
          )}
        </div>

        {/* Result frame */}
        {!ready ? (
          <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl" data-testid="ranking-empty-state">
            <Filter className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Selecione o Ano e a Federação para visualizar o ranking.</p>
            <p className="text-sm mt-1">Os demais filtros são refinamentos opcionais.</p>
          </div>

        /* Loading */
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
          </div>

        /* Athletes tab */
        ) : mode === 'athletes' ? (
          athletes.length === 0 ? (
            <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
              <Medal className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">Nenhum resultado encontrado</p>
              <p className="text-sm mt-1">Altere os filtros ou aguarde resultados de campeonatos</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[#27272A] overflow-hidden" data-testid="ranking-athletes-list">
              {/* FASE 8 — ordem: POS · ATLETA · EQUIPE · PART · PESO · PERFOR · 🥇🥈🥉(2ºB) · PTS
                  Desktop: todas as colunas | Mobile: Equipe e composição ocultas */}
              <div
                className="grid [grid-template-columns:var(--cols)] sm:[grid-template-columns:var(--cols-sm)] gap-1 sm:gap-2 px-1.5 sm:px-4 py-1.5 sm:py-3 bg-[#181818] text-micro sm:text-xs uppercase tracking-wider text-[#71717A] font-semibold"
                style={{ '--cols': grid.mobile, '--cols-sm': grid.desktop }}
              >
                <div className="text-center">Pos</div>
                <div>Atleta</div>
                <div className="hidden sm:block">Equipe</div>
                {bonusComponents && (
                  <>
                    <div className="hidden sm:block text-center" title="Participação: ponto por ter lutado neste campeonato">Part</div>
                    <div className="hidden sm:block text-center" title="Peso do campeonato no ranking (bônus da regra da federação)">Peso</div>
                    <div className="hidden sm:block text-center" title="Performance: bônus por tipo de vitória (pontos, finalização...)">Perfor</div>
                  </>
                )}
                <div className="text-center" title="Medalhas de ouro">🥇</div>
                <div className="text-center" title="Medalhas de prata">🥈</div>
                <div className="text-center" title="Medalhas de bronze">🥉</div>
                {secondBronze && (
                  <div className="flex justify-center items-center" title="2º Bronze (4º lugar — não pontua)">
                    <Medal size={12} className="medalha-segundo-bronze" />
                  </div>
                )}
                <div className="text-center">Pts</div>
              </div>
              {athletes.map((a, idx) => (
                <div
                  key={a.athlete_id}
                  className={`grid [grid-template-columns:var(--cols)] sm:[grid-template-columns:var(--cols-sm)] gap-1 sm:gap-2 px-1.5 sm:px-4 py-1.5 sm:py-3 items-center ${
                    idx % 2 === 1 ? 'bg-[#181818]' : 'bg-[#141414]'
                  } ${idx < 3 ? 'border-l-2 border-l-[#D4AF37]' : ''}`}
                  style={{ '--cols': grid.mobile, '--cols-sm': grid.desktop }}
                  data-testid={`ranking-athlete-row-${idx}`}
                >
                  <div className="flex justify-center">{placeBadge(idx + 1)}</div>
                  <div className="text-white font-medium text-micro sm:text-sm truncate leading-tight">{a.name}</div>
                  <div className="hidden sm:block text-[#A1A1AA] text-xs truncate">{a.team_name || '—'}</div>
                  {bonusComponents && (
                    <>
                      <div className="hidden sm:block text-center text-[#A1A1AA] font-medium text-micro sm:text-sm" title="Participação">{a.participation_points ?? 0}</div>
                      <div className="hidden sm:block text-center text-[#A1A1AA] font-medium text-micro sm:text-sm" title="Peso do campeonato">{a.weight_bonus ?? 0}</div>
                      <div className="hidden sm:block text-center text-[#A1A1AA] font-medium text-micro sm:text-sm" title="Performance por tipo de vitória">{a.performance_points_raw ?? 0}</div>
                    </>
                  )}
                  <div className="text-center medalha-ouro font-bold text-micro sm:text-sm">{a.gold}</div>
                  <div className="text-center medalha-prata font-bold text-micro sm:text-sm">{a.silver}</div>
                  <div className="text-center medalha-bronze font-bold text-micro sm:text-sm">{a.bronze}</div>
                  {secondBronze && (
                    <div className="text-center medalha-segundo-bronze font-bold text-micro sm:text-sm" title="2º Bronze (4º lugar — não pontua)">{a.second_bronze ?? 0}</div>
                  )}
                  <div className="text-center text-white font-bold text-micro sm:text-sm">{a.points}</div>
                </div>
              ))}
              {/* FASE 8 — legenda da composição: o total deixa de ser "solto" */}
              {legend.length > 0 && (
                <div className="px-2 sm:px-4 py-2 bg-[#101010] border-t border-[#27272A] text-micro text-[#71717A] flex flex-wrap gap-x-3 gap-y-1">
                  <span className="text-[#A1A1AA] font-semibold">Como o PTS é formado:</span>
                  {legend.map((l) => (
                    <span key={l.sigla}><span className="text-[#A1A1AA] font-semibold">{l.sigla}</span> = {l.texto}</span>
                  ))}
                  <span>+ os pontos das medalhas (o valor de cada medalha é definido pela regra da federação)</span>
                </div>
              )}
            </div>
          )

        /* Teams tab */
        ) : (
          sortedTeams.length === 0 ? (
            <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
              <Trophy className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">Nenhuma equipe encontrada</p>
              <p className="text-sm mt-1">Altere os filtros ou aguarde resultados</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[#27272A] overflow-hidden" data-testid="ranking-teams-list">
              {/* Mobile: 6 cols (hide Atletas count) | Desktop: 7 cols */}
              <div className="grid grid-cols-[24px_1fr_22px_22px_22px_28px] sm:grid-cols-[50px_1fr_50px_50px_50px_50px_70px] gap-1 sm:gap-2 px-1.5 sm:px-4 py-1.5 sm:py-3 bg-[#181818] text-micro sm:text-xs uppercase tracking-wider text-[#71717A] font-semibold">
                <div className="text-center">Pos</div>
                <div>Equipe</div>
                <div className="text-center">🥇</div>
                <div className="text-center">🥈</div>
                <div className="text-center">🥉</div>
                <div className="hidden sm:block text-center">Atletas</div>
                <div className="text-center">Pts</div>
              </div>
              {sortedTeams.map((t, idx) => (
                <div
                  key={t.team_id}
                  className={`grid grid-cols-[24px_1fr_22px_22px_22px_28px] sm:grid-cols-[50px_1fr_50px_50px_50px_50px_70px] gap-1 sm:gap-2 px-1.5 sm:px-4 py-1.5 sm:py-3 items-center ${
                    idx % 2 === 1 ? 'bg-[#181818]' : 'bg-[#141414]'
                  } ${idx < 3 ? 'border-l-2 border-l-[#D4AF37]' : ''}`}
                  data-testid={`ranking-team-row-${idx}`}
                >
                  <div className="flex justify-center">{placeBadge(idx + 1)}</div>
                  <div className="text-white font-medium text-micro sm:text-sm truncate leading-tight">{t.team_name}</div>
                  <div className="text-center medalha-ouro font-bold text-micro sm:text-sm">{t.gold}</div>
                  <div className="text-center medalha-prata font-bold text-micro sm:text-sm">{t.silver}</div>
                  <div className="text-center medalha-bronze font-bold text-micro sm:text-sm">{t.bronze}</div>
                  <div className="hidden sm:block text-center text-[#A1A1AA] font-bold text-micro sm:text-sm">{t.athletes_count}</div>
                  <div className="text-center text-white font-bold text-micro sm:text-sm">{t.total_points}</div>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}
